import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import ViewWrapper from "./ViewWrapper";
import "../../styles/Views/intro.scss";
import Container2D from "../Cool/Container";

const PortfolioIntro = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = Array.from({ length: 60 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4,
      size: Math.random() * 1.5 + 0.5,
      opacity: Math.random() * 0.5 + 0.1,
    }));

    let animId;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 255, 136, ${p.opacity})`;
        ctx.fill();
      });

      particles.forEach((a, i) => {
        particles.slice(i + 1).forEach((b) => {
          const dist = Math.hypot(a.x - b.x, a.y - b.y);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.strokeStyle = `rgba(0, 255, 136, ${0.08 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        });
      });

      animId = requestAnimationFrame(draw);
    };
    draw();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const badges = [
    "Full-Stack Engineer",
    "SaaS Founder",
    "Cybersecurity Specialist",
    "Open to Remote & Contracts",
  ];

  const stats = [
    { value: "5+", label: "Years Building" },
    { value: "SaaS", label: "Founded & Live" },
    { value: "3", label: "Degree & Certs" },
    { value: "10s", label: "Projects Carried Out" },
  ];

  return (
    <ViewWrapper>
      <div className="intro-hero">
        <canvas ref={canvasRef} className="intro-canvas" />

        <div className="intro-inner">
          <motion.div
            className="intro-badge-row"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {badges.map((b, i) => (
              <motion.span
                key={b}
                className={`intro-badge ${b.includes("Remote") ? "intro-badge--cta" : ""}`}
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: 0.1 * i }}
              >
                {b.includes("Remote") ? "🌐 " : ""}
                {b}
              </motion.span>
            ))}
          </motion.div>

          <motion.div
            className="intro-name-block"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <p className="intro-greeting">Hi, I'm</p>
            <h1 className="intro-name">
              Tamer<span className="intro-name--accent"> Razaq</span>
            </h1>
            <p className="intro-tagline">
              I build production systems that{" "}
              <span className="intro-em">scale, ship, and stay secure.</span>
            </p>
          </motion.div>
          <motion.p
            className="intro-description"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            Full-stack engineer and cybersecurity specialist. Founded and scaled
            a live multi-tenant SaaS platform. Authored a Linux kernel firewall
            module. Co-authoring peer-reviewed research at Tel Aviv University.
            Available for remote contracts and full-time roles worldwide.
          </motion.p>

          <motion.div
            className="intro-stats"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
          >
            {stats.map((s) => (
              <div className="intro-stat" key={s.label}>
                <span className="intro-stat__value">{s.value}</span>
                <span className="intro-stat__label">{s.label}</span>
              </div>
            ))}
          </motion.div>

          <motion.div
            className="intro-cta-row"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
          >
            <a
              href="https://github.com/Tamerabdalrazaq"
              target="_blank"
              rel="noreferrer"
              className="intro-btn intro-btn--ghost"
            >
              GitHub ↗
            </a>
            <a
              href="https://www.linkedin.com/in/tamer-a-532931196/"
              target="_blank"
              rel="noreferrer"
              className="intro-btn intro-btn--ghost"
            >
              LinkedIn ↗
            </a>
          </motion.div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-end",
              gap: "24px",
              padding: "32px",
            }}
          >
            {[
              { text: "PLEASE", color: "#fff" },
              { text: "TAKE", color: "#00ff88" },
              { text: "OFF", color: "#00ff88" },
              { text: "YOUR", color: "#00ff88" },
              { text: "SHOES", color: "#00ff88", cols: 50 },
              { text: "; )", color: "#00ff88" },
            ].map(({ text, color, cols }) => (
              <div
                key={text}
                style={{
                  width: `${text.length * 48}px`,
                  height: "120px",
                }}
              >
                <Container2D
                  pattern={text}
                  options={{ opacity: 1, color, cols }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </ViewWrapper>
  );
};

export default PortfolioIntro;
